import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

export default function TermsPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen" data-testid="terms-page">
      <Header />
      <section className="pt-32 pb-8 bg-muted/50">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-accent transition-colors mb-6">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
          <h1 className="font-heading text-4xl sm:text-5xl font-medium tracking-tighter text-foreground">Terms & Conditions</h1>
          <p className="text-sm text-muted-foreground mt-2">Last updated: April 2026</p>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="space-y-8">
            <div>
              <h2 className="font-heading text-2xl font-medium text-foreground mb-3">1. Introduction</h2>
              <p className="text-sm text-muted-foreground font-light leading-relaxed">
                These Terms and Conditions ("Terms") govern your access to and use of the website www.osaacademy.in ("Website") and the
                services provided by Open Sky Adventure Academy ("OSAA," "we," "us," or "our"). By accessing or using our Website or
                services, you agree to be bound by these Terms. If you do not agree, please do not use the Website.
              </p>
            </div>

            <div>
              <h2 className="font-heading text-2xl font-medium text-foreground mb-3">2. Services</h2>
              <p className="text-sm text-muted-foreground font-light leading-relaxed">
                OSAA provides experiential learning programs for schools, including but not limited to: AgriLearn Campus (agriculture and
                ecology), AeroLab Experience (aviation and aerospace), and SailScience Program (maritime science and sailing). These
                programs are designed for students from Nursery through Grade 12 and are delivered in partnership with schools and
                educational institutions.
              </p>
            </div>

            <div>
              <h2 className="font-heading text-2xl font-medium text-foreground mb-3">3. Inquiries, Proposals & Bookings</h2>
              <p className="text-sm text-muted-foreground font-light leading-relaxed">
                Submitting an inquiry through our Website does not constitute a booking. All bookings are subject to availability, slot
                clearance (particularly for AeroLab Experience at Jakkur Aerodrome), and confirmation by OSAA. A formal booking is
                confirmed only upon receipt of a signed letter of intent from the school and confirmation from OSAA.
              </p>
            </div>

            <div>
              <h2 className="font-heading text-2xl font-medium text-foreground mb-3">4. Payments, Cancellations & Refunds</h2>
              <ul className="space-y-2 text-sm text-muted-foreground font-light">
                <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" /><span>Payment terms are communicated upon booking confirmation and are outlined in the institutional proposal.</span></li>
                <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" /><span>Cancellations must be communicated in writing. Cancellation charges may apply depending on the notice period.</span></li>
                <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" /><span>Refunds, if applicable, will be processed within 30 business days to the original payment method.</span></li>
                <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" /><span>OSAA reserves the right to cancel or reschedule programs due to weather conditions, security restrictions, or other unforeseen circumstances.</span></li>
              </ul>
            </div>

            <div>
              <h2 className="font-heading text-2xl font-medium text-foreground mb-3">5. Safety, Risk & Liability</h2>
              <p className="text-sm text-muted-foreground font-light leading-relaxed mb-3">
                While OSAA maintains the highest safety standards, participation in experiential learning programs involves inherent risks
                associated with outdoor activities, interaction with farm animals, aviation environments, and water-based activities.
              </p>
              <ul className="space-y-2 text-sm text-muted-foreground font-light">
                <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" /><span><strong className="text-foreground">Acknowledgement of Risk:</strong> By booking a program, the school acknowledges these inherent risks on behalf of participants.</span></li>
                <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" /><span><strong className="text-foreground">Release & Waiver:</strong> Schools agree to release OSAA from liability for injuries arising from normal program activities, except in cases of gross negligence.</span></li>
                <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" /><span><strong className="text-foreground">Indemnification:</strong> Schools agree to indemnify OSAA against claims arising from the school's own negligence or misconduct.</span></li>
                <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" /><span><strong className="text-foreground">Medical & Emergency:</strong> OSAA provides on-site first aid and emergency protocols. Schools must inform OSAA of any medical conditions or allergies in advance.</span></li>
              </ul>
            </div>

            <div>
              <h2 className="font-heading text-2xl font-medium text-foreground mb-3">6. Conduct & Supervision</h2>
              <ul className="space-y-2 text-sm text-muted-foreground font-light">
                <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" /><span>Our campus and program venues are intentionally device-free. Mobile phones and electronic devices must be surrendered during program hours.</span></li>
                <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" /><span>All participants must adhere to the safety rules and instructions provided by OSAA facilitators at all times.</span></li>
                <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" /><span>Schools are required to provide accompanying faculty members as per agreed ratios.</span></li>
                <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" /><span>OSAA reserves the right to remove any participant whose conduct poses a risk to themselves or others.</span></li>
              </ul>
            </div>

            <div>
              <h2 className="font-heading text-2xl font-medium text-foreground mb-3">7. Intellectual Property</h2>
              <p className="text-sm text-muted-foreground font-light leading-relaxed">
                All content on this Website, including text, graphics, logos, images, program materials, and software, is the property of
                Open Sky Adventure Academy and is protected by applicable intellectual property laws. You may not reproduce, distribute,
                or create derivative works from any content without our prior written consent.
              </p>
            </div>

            <div>
              <h2 className="font-heading text-2xl font-medium text-foreground mb-3">8. Photography & Media</h2>
              <p className="text-sm text-muted-foreground font-light leading-relaxed">
                OSAA may capture photographs and videos during programs for educational documentation, promotional materials, and social
                media. By participating, schools consent to the use of such media. Schools may opt out of media usage by providing written
                notice prior to the program date.
              </p>
            </div>

            <div>
              <h2 className="font-heading text-2xl font-medium text-foreground mb-3">9. Limitation of Liability</h2>
              <p className="text-sm text-muted-foreground font-light leading-relaxed">
                To the maximum extent permitted by law, OSAA's total liability for any claims arising from our services shall not exceed
                the total amount paid by the school for the specific program in question. OSAA shall not be liable for any indirect,
                incidental, consequential, or punitive damages.
              </p>
            </div>

            <div>
              <h2 className="font-heading text-2xl font-medium text-foreground mb-3">10. Force Majeure</h2>
              <p className="text-sm text-muted-foreground font-light leading-relaxed">
                OSAA shall not be liable for any failure or delay in performing our obligations due to circumstances beyond our reasonable
                control, including but not limited to natural disasters, government restrictions, pandemics, civil unrest, or adverse
                weather conditions.
              </p>
            </div>

            <div>
              <h2 className="font-heading text-2xl font-medium text-foreground mb-3">11. Governing Law & Dispute Resolution</h2>
              <p className="text-sm text-muted-foreground font-light leading-relaxed">
                These Terms are governed by the laws of India. Any disputes arising from these Terms or our services shall be subject to
                the exclusive jurisdiction of the courts in Bengaluru, Karnataka, India.
              </p>
            </div>

            <div>
              <h2 className="font-heading text-2xl font-medium text-foreground mb-3">12. Contact Us</h2>
              <p className="text-sm text-muted-foreground font-light leading-relaxed">
                For questions regarding these Terms, please contact us:
              </p>
              <div className="mt-3 p-4 border border-border/60 bg-card">
                <p className="text-sm text-foreground font-medium">Open Sky Adventure Academy</p>
                <p className="text-sm text-muted-foreground font-light">Email: info@osaacademy.in</p>
                <p className="text-sm text-muted-foreground font-light">Phone: +91 8618396055</p>
                <p className="text-sm text-muted-foreground font-light">Grievance Officer: privacy@osaacademy.in</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
